import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Sidebar — the navigation column every app's shell is built on (ADR-0088
 * D2). Each app composes it with its own navigation: Admin's operator tools,
 * Manager's dashboard sections, Student's learner pages.
 *
 * It replaces Admin's requirement-shaped slice, Student's shadcn sidebar and
 * Manager's hand-built one. What it owns:
 *
 * - **State.** Expanded or collapsed to an icon rail, controlled by the app,
 *   which keeps its own preference (a cookie, local storage). Cmd/Ctrl+B
 *   toggles it, except while someone types in a field.
 * - **A narrow window.** Below `narrowQuery` the sidebar is a left sheet that
 *   a trigger opens, focus returning to that trigger when it closes. Choosing
 *   a menu item closes it; so does widening the window.
 * - **Opening over the page**, when an app asks (`overlay`, Student's
 *   tablets). The rail keeps its place and the page never narrows: the
 *   expanded column floats over it with the overlay shadow and a scrim, and a
 *   click on the scrim, Escape, a chosen item or the brand, or focus moving
 *   into the page it covers collapses it.
 * - **The rail's labels.** A collapsed menu button shows its label as a
 *   tooltip, and keeps it as its accessible name.
 *
 * One look and one size in every app: a 56 px header level with the top bar,
 * items in the secondary text with a neutral hover, the current item in
 * `accent-soft`, 18 px icons. The input device sets the density, not the app:
 * with a mouse or trackpad, 36 px items in a 64 px rail; on a touch screen
 * (`pointer: coarse`), the 44 px touch target in a 72 px rail. Both rails and
 * the column grow by a notch's left inset.
 *
 * Nothing moves sideways when the column collapses or expands: every part is
 * anchored 14 px from the left edge in both states, and the rail is exactly
 * that inset, an item and the inset again, so an icon sits centered on the
 * rail without being re-centered, and an item keeps its full width. Only the
 * width animates: the shrinking column clips the labels as they fade, and
 * group headings fade and fold away, over the same 200 ms. On expansion,
 * the selected yellow tile holds for 150 ms, then spreads to the soft full-row
 * fill over 350 ms, overlapping the column’s ease-out finish. Reduced motion
 * shows the final state immediately. Collapse reverses the fill over 150 ms,
 * overlapping its last 75 ms with a 300 ms eased column close. The collapsed classes
 * follow the state in React rather than CSS selectors, so a part looks
 * collapsed exactly when the rail is; the parts also carry shadcn's data
 * attributes (`data-slot`, `data-sidebar`, `data-state`, `data-collapsible`)
 * for an app's own layout rules.
 *
 * On a wide window the column is fixed beside a spacer of the same width, so
 * the page scrolls under nothing and the column never scrolls away.
 */
import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { Button } from './button.js';
import { cn } from './cn.js';
import { Icon } from './icon.js';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from './sheet.js';
import { Tooltip } from './tooltip.js';
const SidebarContext = createContext(null);
export function useSidebar() {
    const context = useContext(SidebarContext);
    if (!context)
        throw new Error('useSidebar must be used within SidebarProvider');
    return context;
}
/** True while the rail shows icons only: collapsed, on a wide window. */
function useCollapsedRail() {
    const { state, isNarrow } = useSidebar();
    return state === 'collapsed' && !isNarrow;
}
function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => typeof window === 'undefined' ? false : window.matchMedia(query).matches);
    useEffect(() => {
        const list = window.matchMedia(query);
        // Read afresh on each change rather than from the event, which some
        // environments do not pass.
        const update = () => setMatches(window.matchMedia(query).matches);
        update();
        list.addEventListener('change', update);
        return () => list.removeEventListener('change', update);
    }, [query]);
    return matches;
}
/**
 * The column's widths, plus a landscape notch's left inset. An app may set
 * `--sidebar-width` itself.
 */
const widths = '[--sidebar-width:calc(15rem_+_env(safe-area-inset-left))] ' +
    '[--sidebar-width-icon:calc(4rem_+_env(safe-area-inset-left))] ' +
    'pointer-coarse:[--sidebar-width-icon:calc(4.5rem_+_env(safe-area-inset-left))]';
export function SidebarProvider({ narrowQuery, defaultOpen = true, overlay = false, open: controlledOpen, onOpenChange, className, style, children, ...props }) {
    const isNarrow = useMediaQuery(narrowQuery);
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const [openNarrow, setOpenNarrow] = useState(false);
    const triggerRef = useRef(null);
    const open = controlledOpen ?? internalOpen;
    const [selectionReady, setSelectionReady] = useState(open);
    const selectionExpanded = isNarrow || (open && (reducedMotion || selectionReady));
    useEffect(() => {
        if (!open || isNarrow || reducedMotion) {
            setSelectionReady(open);
            return;
        }
        // Start 50 ms before the 200 ms width transition ends. Cancel on reversal
        // so a quick collapse/reopen never inherits a pending highlight.
        const timeout = window.setTimeout(() => setSelectionReady(true), 150);
        return () => window.clearTimeout(timeout);
    }, [open, isNarrow, reducedMotion]);
    // A sheet must not survive into the wide layout: returning below the
    // breakpoint would remount it open and take focus with no new action.
    useEffect(() => {
        if (!isNarrow)
            setOpenNarrow(false);
    }, [isNarrow]);
    const setOpen = useCallback((next) => {
        if (onOpenChange)
            onOpenChange(next);
        else
            setInternalOpen(next);
    }, [onOpenChange]);
    const toggleSidebar = useCallback(() => {
        if (isNarrow)
            setOpenNarrow((current) => !current);
        else
            setOpen(!open);
    }, [isNarrow, open, setOpen]);
    useEffect(() => {
        const toggleWithKeyboard = (event) => {
            const target = event.target;
            const editing = target instanceof Element &&
                target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])') !== null;
            if (!editing && event.key.toLowerCase() === 'b' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                toggleSidebar();
            }
        };
        window.addEventListener('keydown', toggleWithKeyboard);
        return () => window.removeEventListener('keydown', toggleWithKeyboard);
    }, [toggleSidebar]);
    // Open over the page, the column is dismissed like any overlay, one layer
    // per Escape: a popup or dialog opened above it (the account menu, its
    // sign-out confirmation) renders outside the shell and takes the key.
    const floating = overlay && open && !isNarrow;
    useEffect(() => {
        if (!floating)
            return;
        const closeOnEscape = (event) => {
            if (event.key !== 'Escape' || event.defaultPrevented)
                return;
            const target = event.target;
            const outsideShell = target instanceof Element &&
                target !== document.body &&
                target.closest('[data-slot="sidebar-wrapper"]') === null;
            if (!outsideShell)
                setOpen(false);
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [floating, setOpen]);
    const dismiss = useCallback(() => {
        if (isNarrow)
            setOpenNarrow(false);
        else if (overlay && open)
            setOpen(false);
    }, [isNarrow, overlay, open, setOpen]);
    const value = useMemo(() => ({
        state: open ? 'expanded' : 'collapsed',
        open,
        setOpen,
        isNarrow,
        overlay,
        dismiss,
        openNarrow,
        setOpenNarrow,
        toggleSidebar,
        triggerRef,
        selectionExpanded,
    }), [open, setOpen, isNarrow, overlay, dismiss, openNarrow, toggleSidebar, selectionExpanded]);
    return (_jsx(SidebarContext.Provider, { value: value, children: _jsx("div", { "data-slot": "sidebar-wrapper", "data-state": value.state, style: style, className: cn('group/sidebar-wrapper flex min-h-svh w-full bg-surface-app', widths, className), ...props, children: children }) }));
}
/**
 * The column. `className` styles the visible column: the fixed panel on a
 * wide window, the sheet on a narrow one.
 */
export function Sidebar({ narrowTitle, narrowDescription, className, children, onBlur, ...props }) {
    const { state, isNarrow, overlay, setOpen, openNarrow, setOpenNarrow, triggerRef } = useSidebar();
    if (isNarrow) {
        return (_jsx(Sheet, { open: openNarrow, onOpenChange: setOpenNarrow, children: _jsxs(SheetContent, { side: "left", showCloseButton: false, "data-slot": "sidebar", "data-sidebar": "sidebar", "data-mobile": "true", finalFocus: triggerRef, className: cn('pl-[env(safe-area-inset-left)]', className), onBlur: onBlur, ...props, children: [_jsx(SheetTitle, { className: "sr-only", children: narrowTitle }), narrowDescription !== undefined && (_jsx(SheetDescription, { className: "sr-only", children: narrowDescription })), _jsx("div", { className: "flex h-full w-full flex-col", children: children })] }) }));
    }
    const collapsed = state === 'collapsed';
    const floating = overlay && !collapsed;
    return (_jsxs("div", { className: "group peer text-text-primary", "data-slot": "sidebar", "data-state": state, "data-collapsible": collapsed ? 'icon' : '', "data-side": "left", "data-overlay": overlay || undefined, children: [_jsx("div", { "data-slot": "sidebar-gap", className: cn('relative bg-transparent transition-[width] duration-200 ease-out motion-reduce:transition-none', collapsed || overlay ? 'w-(--sidebar-width-icon)' : 'w-(--sidebar-width)', collapsed && 'delay-75 duration-300 ease-out motion-reduce:delay-0') }), floating ? (
            // Under the column and over the page (whose stacking context sits
            // below the column's z-10): a click on it collapses the column.
            _jsx("div", { "data-slot": "sidebar-scrim", "aria-hidden": "true", className: "fixed inset-0 z-[9] bg-overlay-scrim transition-opacity duration-200 starting:opacity-0 motion-reduce:transition-none", onClick: () => setOpen(false) })) : null, _jsx("div", { "data-slot": "sidebar-container", "data-side": "left", className: cn(
                // The edge line is drawn over the column's last pixel, not as a
                // border: a border took its pixel from the rail, leaving every item
                // 1 px short of 36 px (44 px on touch) and off centre.
                'fixed inset-y-0 left-0 z-10 box-border flex h-svh bg-surface-card pl-[env(safe-area-inset-left)]', "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-border-default after:content-['']", 'transition-[width] duration-200 ease-out motion-reduce:transition-none', collapsed ? 'w-(--sidebar-width-icon)' : 'w-(--sidebar-width)', 
                // Overlap the last 75 ms of the fill shrink with a gentle column close.
                collapsed && 'delay-75 duration-300 ease-out motion-reduce:delay-0', floating && 'shadow-menu', className), ...props, onBlur: (event) => {
                    onBlur?.(event);
                    // Open over the page, the column steps aside once focus moves into
                    // the page it covers, so a keyboard never reaches a hidden control.
                    // A popup it opens (the account menu) renders outside the shell,
                    // and moving into one keeps the column open.
                    const next = event.relatedTarget;
                    if (!floating || !(next instanceof Node) || event.currentTarget.contains(next))
                        return;
                    if (event.currentTarget.closest('[data-slot="sidebar-wrapper"]')?.contains(next))
                        setOpen(false);
                }, children: _jsx("div", { "data-sidebar": "sidebar", "data-slot": "sidebar-inner", className: "flex size-full flex-col bg-surface-card", children: children }) })] }));
}
/** The page beside the sidebar. Student's is the `<main>` landmark; Admin's and Manager's pages hold their own. */
export function SidebarInset({ as: Element = 'div', className, ...props }) {
    return (_jsx(Element, { "data-slot": "sidebar-inset", className: cn('relative flex min-h-svh w-full min-w-0 flex-1 flex-col bg-surface-app', className), ...props }));
}
/**
 * Collapses or expands the column, or opens the narrow sheet. On a wide
 * window its tooltip names the shortcut; on a narrow one, where the sheet
 * may hold a trigger of its own that takes focus as it opens, it has none.
 */
export function SidebarTrigger({ label, icon: iconOverride, className, onClick, ...props }) {
    const { state, isNarrow, openNarrow, toggleSidebar, triggerRef } = useSidebar();
    // The glyph says what a click does: on a narrow window the sheet decides
    // (the top bar's trigger opens it, the sheet's own toggle closes it).
    const opens = isNarrow ? !openNarrow : state === 'collapsed';
    const icon = iconOverride ?? (opens ? 'sidebarOpen' : 'sidebarClose');
    return (_jsx(Tooltip, { label: `${label} (⌘B)`, side: "right", disabled: isNarrow, children: _jsx(Button, { "data-slot": "sidebar-trigger", "data-sidebar": "trigger", "aria-label": label, variant: "ghost", size: "icon", className: cn('shrink-0 pointer-coarse:size-11', className), onClick: (event) => {
                onClick?.(event);
                // Focus returns to the button that opened the sheet, not to the
                // sheet's own toggle that closes it.
                if (!(isNarrow && openNarrow))
                    triggerRef.current = event.currentTarget;
                toggleSidebar();
            }, ...props, children: _jsx(Icon, { name: icon }) }) }));
}
/**
 * The header is as high as the app's top bar, so their lower borders meet in
 * one line; a notch's top inset, which a phone sheet pads past, may grow it.
 */
const headerClassName = 'flex min-h-14 shrink-0 items-center overflow-hidden border-b border-border-default px-3.5';
/** Clips what does not fit yet while the column animates; never re-centers. */
export function SidebarHeader({ className, ...props }) {
    return _jsx("div", { "data-slot": "sidebar-header", "data-sidebar": "header", className: cn(headerClassName, className), ...props });
}
export function SidebarContent({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sidebar-content", "data-sidebar": "content", className: cn('min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-3.5 py-3', className), ...props }));
}
export function SidebarFooter({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sidebar-footer", "data-sidebar": "footer", className: cn('shrink-0 overflow-hidden border-t border-border-faint p-2', className), ...props }));
}
export function SidebarGroup({ className, ...props }) {
    return _jsx("div", { "data-slot": "sidebar-group", "data-sidebar": "group", className: cn('mb-6', className), ...props });
}
/**
 * A group's heading, on the items' icon column. On the rail it fades and
 * folds its 24 px away (a negative margin as tall as it is), so the items
 * glide up rather than jump; assistive technology still reads it.
 */
export function SidebarGroupLabel({ className, ...props }) {
    const collapsed = useCollapsedRail();
    return (_jsx("h2", { "data-slot": "sidebar-group-label", "data-sidebar": "group-label", className: cn('mb-2 h-4 overflow-hidden whitespace-nowrap px-[9px] text-label font-semibold uppercase tracking-eyebrow text-text-faint', 'transition-[margin,opacity] duration-200 ease-linear motion-reduce:transition-none', collapsed ? 'pointer-events-none -mt-6 opacity-0' : 'mt-0', className), ...props }));
}
export function SidebarGroupContent({ className, ...props }) {
    return (_jsx("div", { "data-slot": "sidebar-group-content", "data-sidebar": "group-content", className: cn('w-full text-body', className), ...props }));
}
export function SidebarMenu({ className, ...props }) {
    return (_jsx("ul", { "data-slot": "sidebar-menu", "data-sidebar": "menu", className: cn('m-0 list-none space-y-0.5 p-0', className), ...props }));
}
export function SidebarMenuItem({ className, ...props }) {
    return _jsx("li", { "data-slot": "sidebar-menu-item", "data-sidebar": "menu-item", className: cn('group/menu-item relative', className), ...props });
}
/** Shared content inset for sidebar links, identity and inline preferences. */
export const sidebarRowPadding = 'pl-[9px] pr-3 pointer-coarse:pl-[13px]';
/**
 * One shape in both states: a full-width item whose icon sits 9 px in (13 px
 * on touch), which on the rail is a 36 px (44 px) square with the icon
 * centered. Its label stays in the accessibility tree; on the rail it fades
 * out while the column clips it.
 */
const menuButton = {
    base: sidebarRowPadding + ' ' +
        'relative isolate flex min-h-9 w-full items-center gap-3 overflow-hidden rounded-token-input text-body no-underline transition-colors ' +
        'pointer-coarse:min-h-11 ' +
        '[&>span]:min-w-0 [&>span]:truncate [&>span]:transition-opacity [&>span]:duration-200 [&>span]:ease-linear ' +
        'motion-reduce:[&>span]:transition-none ' +
        'focus-visible:focus-ring-inset ' +
        'text-text-secondary not-data-active:hover:bg-surface-card-hover not-data-active:hover:text-text-primary ' +
        'data-active:font-medium data-active:text-text-primary data-active:transition-none ' +
        '[&>svg]:size-[1.125rem] [&>svg]:shrink-0 [&>svg]:transition-colors [&>svg]:duration-350 [&>svg]:ease-in-out motion-reduce:[&>svg]:transition-none ' +
        // A separate layer can hold the square while the column grows around it.
        "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:-z-10 before:rounded-token-input before:content-[''] " +
        'before:transition-[width,background-color] before:duration-350 before:ease-in-out motion-reduce:before:transition-none ' +
        'duration-200 ease-linear motion-reduce:transition-none',
    // Expanded uses the theme's foreground on the soft tint. On the rail, the
    // solid yellow fill always carries on-accent ink, including in dark mode.
    // There the yellow inset ring would vanish into the fill, so focus parts
    // them with a 2 px gap in the sidebar's white, as a focused yellow button's
    // ring is parted from its fill, drawn inward because the rail clips.
    collapsed: '[&>span]:opacity-0 data-active:text-on-accent motion-safe:data-active:transition-[color] motion-safe:data-active:duration-150 motion-safe:data-active:ease-in-out ' +
        'data-active:focus-visible:before:shadow-[inset_0_0_0_4px_var(--color-surface-card)]',
    selectionExpanded: 'before:w-full data-active:before:bg-accent-soft data-active:[&>svg]:text-text-primary',
    selectionTile: 'before:w-9 pointer-coarse:before:w-11 before:duration-150 [&>svg]:duration-150 data-active:before:bg-accent data-active:[&>svg]:text-on-accent',
};
/**
 * A menu item's control: a link through `render` (`<Link to="…" />`), or a
 * button. It closes the narrow sheet, or a column open over the page, when
 * chosen.
 */
export function SidebarMenuButton({ render, isActive = false, tooltip, className, onClick, ...props }) {
    const { dismiss, selectionExpanded } = useSidebar();
    const collapsed = useCollapsedRail();
    const element = useRender({
        defaultTagName: 'button',
        render,
        props: mergeProps({
            className: cn(menuButton.base, collapsed && menuButton.collapsed, selectionExpanded ? menuButton.selectionExpanded : menuButton.selectionTile, className),
            onClick: (event) => {
                onClick?.(event);
                dismiss();
            },
        }, props),
        state: { slot: 'sidebar-menu-button', sidebar: 'menu-button', active: isActive, selection: selectionExpanded ? 'expanded' : 'tile' },
    });
    if (tooltip === undefined)
        return element;
    return (
    // Both rails leave 14 px between an item and their edge: 18 px puts the
    // label clear of the rail.
    _jsx(Tooltip, { label: tooltip, side: "right", sideOffset: 18, disabled: !collapsed, children: element }));
}
//# sourceMappingURL=sidebar.js.map